import { useRef, useEffect, useCallback, useState } from 'react';
import { SerialMessage, useSerial } from 'contexts/SerialProvider';
import { useScannerContext } from 'contexts/ScannerContext';
import {
  CounterOutput,
  MessageLogEntry,
  parseSerialMessage,
  createMessageLogEntry,
  CounterMessageError,
  CounterMessageErrorType,
} from 'views/warehouse/Counting/hooks/utils';

export type SerialType = ReturnType<typeof useSerial>;

export interface UseCounterSerialMessagesReturn {
  serial: SerialType;
  getMessageForLotId: (lotId: string) => CounterOutput | null;
  clearAllMessages: () => void;
  messageHistory: MessageLogEntry[];
  clearMessageHistory: () => void;
}

const MAX_LOG_ENTRIES = 100;
const MAX_BUFFER_SIZE = 1000;

/**
 * Custom hook to handle serial messages for inventory counting
 * Processes incoming serial messages and integrates with the ScannerContext
 */
export const useCounterSerialMessages = (): UseCounterSerialMessagesReturn => {
  const messageBuffer = useRef('');
  const messagesByLotId = useRef<Record<string, CounterOutput>>({});
  const serial = useSerial();
  const { setScannedStringHandler } = useScannerContext();

  const [messageHistory, setMessageHistory] = useState<MessageLogEntry[]>([]);

  const addToMessageHistory = useCallback((entry: MessageLogEntry) => {
    setMessageHistory((prev) => {
      const newHistory = [entry, ...prev];
      return newHistory.slice(0, MAX_LOG_ENTRIES);
    });
  }, []);

  /**
   * Handles incoming serial messages, accumulates them in the buffer,
   * and attempts to parse them as JSON
   */
  const handleSerialMessage = useCallback(
    (message: SerialMessage) => {
      const rawMessage = message.value?.trim() || '';

      if (messageBuffer.current.length + rawMessage.length > MAX_BUFFER_SIZE) {
        addToMessageHistory(
          createMessageLogEntry(
            messageBuffer.current,
            undefined,
            'Buffer overflow - clearing'
          )
        );
        messageBuffer.current = rawMessage;
        return;
      }

      messageBuffer.current += rawMessage;

      try {
        const parsedMessage = parseSerialMessage(messageBuffer.current);
        const originalMessage = messageBuffer.current;
        messageBuffer.current = '';

        addToMessageHistory(
          createMessageLogEntry(originalMessage, parsedMessage)
        );

        if (parsedMessage.lotid) {
          messagesByLotId.current = {
            ...messagesByLotId.current,
            [parsedMessage.lotid]: parsedMessage,
          };

          setScannedStringHandler(parsedMessage.lotid);
        }
      } catch (error) {
        if (error instanceof CounterMessageError) {
          // Only log complete message failures, not partial messages
          if (error.message !== CounterMessageErrorType.INVALID_JSON) {
            addToMessageHistory(
              createMessageLogEntry(error.rawMessage, undefined, error.message)
            );
            messageBuffer.current = '';
          }
        } else {
          addToMessageHistory(
            createMessageLogEntry(
              messageBuffer.current,
              undefined,
              `Unexpected error: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            )
          );
          messageBuffer.current = '';
        }
      }
    },
    [setScannedStringHandler, addToMessageHistory]
  );

  useEffect(() => {
    const unsubscribe = serial.subscribe(handleSerialMessage);
    return () => {
      unsubscribe();
      messageBuffer.current = '';
    };
  }, [handleSerialMessage, serial]);

  const getMessageForLotId = useCallback(
    (lotId: string): CounterOutput | null => {
      const message = messagesByLotId.current[lotId];
      if (message) {
        const { [lotId]: __, ...rest } = messagesByLotId.current;
        messagesByLotId.current = rest;
        return message;
      }
      return null;
    },
    []
  );

  const clearAllMessages = useCallback(() => {
    messagesByLotId.current = {};
  }, []);

  const clearMessageHistory = useCallback(() => {
    setMessageHistory([]);
  }, []);

  return {
    getMessageForLotId,
    serial,
    clearAllMessages,
    messageHistory,
    clearMessageHistory,
  };
};
